/**
 * Non-component helpers for the voice window: the state → tag mapping, the
 * responsive layout rule, the captions preference and the elapsed clock.
 */
import type { RealtimeAudioState } from "../../hooks/use-realtime-audio"
import type { VoiceRingState } from "../voice-activity-ring-utils"

import { getInjectedVoiceStage } from "@/lib/runtime-branding"

/**
 * The transcript sits beside the presence only when the viewport can hold the
 * 42 rem window plus the 22 rem wall and the dialog's 2 rem margin (66 rem).
 * Below that it sits under the presence. Decided from the viewport, not the
 * window's current width, so a closed 42 rem window still opens beside.
 */
export const VOICE_STAGE_BESIDE_MIN_VIEWPORT_PX = 1056

export const VOICE_STAGE_BESIDE_MEDIA_QUERY = `(min-width: ${VOICE_STAGE_BESIDE_MIN_VIEWPORT_PX}px)`

/** The colour of the lines and rings drawn on the ground. */
export type VoiceStageInk = "dark" | "light"

/**
 * What the window sits on: the brand's sky unless the tenant set a flat colour
 * or an image in branding (admin, Branding, Voice window).
 */
export type VoiceStageBackground =
  | { mode: "sky" }
  | { mode: "color"; color: string }
  | { mode: "image"; url: string; ink?: VoiceStageInk }

export const DEFAULT_VOICE_STAGE_BACKGROUND: VoiceStageBackground = { mode: "sky" }

/** The tenant's ground as the server injected it, else the sky. */
export function resolveVoiceStageBackground(): VoiceStageBackground {
  return getInjectedVoiceStage() ?? DEFAULT_VOICE_STAGE_BACKGROUND
}

// The stage's own ink and paper (voice-stage.module.css: --ink, --paper).
const STAGE_INK_RGB: [number, number, number] = [53, 58, 59]
const STAGE_PAPER_RGB: [number, number, number] = [252, 250, 247]

function relativeLuminance([red, green, blue]: [number, number, number]): number {
  const channel = (value: number) => {
    const normalized = value / 255
    return normalized <= 0.03928 ? normalized / 12.92 : ((normalized + 0.055) / 1.055) ** 2.4
  }
  return 0.2126 * channel(red) + 0.7152 * channel(green) + 0.0722 * channel(blue)
}

function contrastRatio(a: number, b: number): number {
  return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05)
}

function hexToRgb(hex: string): [number, number, number] | null {
  const match = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim())
  if (!match) return null
  const digits =
    match[1].length === 3
      ? match[1]
          .split("")
          .map((digit) => digit + digit)
          .join("")
      : match[1]
  return [0, 2, 4].map((offset) => parseInt(digits.slice(offset, offset + 2), 16)) as [
    number,
    number,
    number,
  ]
}

/**
 * The ink that reads on this ground. On a flat colour it is whichever of the
 * stage's ink and paper contrasts more with it, so a navy or a forest green
 * gets light lines and a pale tint keeps dark ones. Over a photo only the
 * admin can judge, so the setting carries it.
 */
export function resolveStageInk(background: VoiceStageBackground): VoiceStageInk {
  if (background.mode === "image") return background.ink ?? "light"
  if (background.mode !== "color") return "dark"
  const rgb = hexToRgb(background.color)
  if (!rgb) return "dark"
  const ground = relativeLuminance(rgb)
  const onInk = contrastRatio(ground, relativeLuminance(STAGE_INK_RGB))
  const onPaper = contrastRatio(ground, relativeLuminance(STAGE_PAPER_RGB))
  return onPaper > onInk ? "light" : "dark"
}

/** The one word the presence tag shows. Tone follows the brand's node colours. */
export type VoicePresenceTag = {
  /** `voice_mode.tags.*` key. */
  key:
    | "idle"
    | "connecting"
    | "listening"
    | "thinking"
    | "searching"
    | "responding"
    | "interrupted"
    | "reconnecting"
    | "unavailable"
  tone: "outlined" | "primary" | "warning" | "quiet" | "destructive"
  /** `voice_mode.hints.*` key for the functional node under the tag, if any. */
  hint: "speak_anytime" | "listening_to_you" | "go_ahead" | "retry_once" | null
  /** Who is speaking, for the rings. */
  voice: "user" | "assistant" | null
}

export function resolvePresenceTag(
  ringState: VoiceRingState,
  state: RealtimeAudioState
): VoicePresenceTag {
  switch (ringState) {
    case "connecting":
      return { key: "connecting", tone: "quiet", hint: null, voice: null }
    case "listening":
      return state === "user_speaking"
        ? { key: "listening", tone: "outlined", hint: "listening_to_you", voice: "user" }
        : { key: "listening", tone: "outlined", hint: "speak_anytime", voice: null }
    case "thinking":
      return { key: "thinking", tone: "outlined", hint: null, voice: null }
    case "tool":
      return { key: "searching", tone: "warning", hint: null, voice: null }
    case "speaking":
      return { key: "responding", tone: "primary", hint: null, voice: "assistant" }
    case "interrupted":
      return { key: "interrupted", tone: "outlined", hint: "go_ahead", voice: "user" }
    case "reconnecting":
      return { key: "reconnecting", tone: "quiet", hint: "retry_once", voice: null }
    case "error":
      return { key: "unavailable", tone: "destructive", hint: null, voice: null }
    default:
      return { key: "idle", tone: "outlined", hint: null, voice: null }
  }
}

/** Whether the transcript goes below the presence at this viewport width. */
export function isStackedLayout(viewportWidthPx: number): boolean {
  return viewportWidthPx < VOICE_STAGE_BESIDE_MIN_VIEWPORT_PX
}

const CAPTIONS_STORAGE_KEY = "helloai.voice.captions"

/**
 * The captions preference is per device, on for the first call so the panel
 * is discovered, then whatever the user last chose.
 */
export function readCaptionsPreference(): boolean {
  try {
    const stored = window.localStorage.getItem(CAPTIONS_STORAGE_KEY)
    return stored === null ? true : stored === "on"
  } catch {
    return true
  }
}

export function writeCaptionsPreference(open: boolean): void {
  try {
    window.localStorage.setItem(CAPTIONS_STORAGE_KEY, open ? "on" : "off")
  } catch {
    // Storage can be unavailable (private mode, blocked site data); the
    // choice then lasts for the session only.
  }
}

/** `mm:ss` for the header clock, clamped so a skewed clock never renders negative. */
export function formatClock(ms: number): string {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000))
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`
}

/** Markdown emphasis and headings, which activity text carries but a pill cannot render. */
function plainActivityText(text: string): string {
  return text
    .replace(/\*\*|__|`/g, "")
    .replace(/^#+\s*/, "")
    .replace(/\s+/g, " ")
    .trim()
}

/**
 * A reasoning line arrives as "**Title** the model's note"; the title is the
 * step and the note is detail for the transcript. Anything else is one line.
 */
export function splitActivityText(text: string): { title: string; note: string | null } {
  const bold = /^\s*\*\*(.+?)\*\*\s*(.*)$/s.exec(text)
  if (bold) {
    const note = plainActivityText(bold[2])
    return { title: plainActivityText(bold[1]), note: note || null }
  }
  return { title: plainActivityText(text), note: null }
}
